import { useState } from 'react';
import { MapPin, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contato = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Função para rastrear cliques
  const trackContact = async (type) => {
    try {
      await axios.post(`${BACKEND_URL}/api/track/contact?type=${type}`);
    } catch (e) {
      console.log('Track error');
    }
  };

  const handleEmailClick = () => {
    trackContact('email');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Track email contact
    trackContact('email');
    
    // Simulação de envio por email
    const emailBody = encodeURIComponent(
      `Nome: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Telefone: ${formData.phone}\n` +
      `Assunto: ${formData.subject}\n\n` +
      `Mensagem:\n${formData.message}`
    );
    
    window.location.href = `mailto:${companyInfo.email}?subject=${encodeURIComponent(formData.subject)}&body=${emailBody}`;
    
    toast({
      title: "Redirecionando para seu cliente de email",
      description: "Complete o envio através do seu aplicativo de email.",
    });
  };

  // Função para formatar telefone automaticamente
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Celular: (00) 00000-0000
      return numbers
        .slice(0, 11)
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('Olá! Gostaria de mais informações.');
    window.location.href = `https://wa.me/${companyInfo.whatsapp}?text=${message}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Entre em Contato</h1>
          <p className="text-xl text-blue-100">
            Estamos prontos para atender você
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Informações de Contato</h2>
              <p className="text-gray-600 mb-8">
                Entre em contato conosco através dos canais abaixo ou preencha o formulário. 
                Teremos prazer em ajudá-lo!
              </p>

              <div className="space-y-6 mb-8">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-red-600 p-3 rounded-lg flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Endereço</h3>
                      <p className="text-gray-600">
                        {companyInfo.address}<br />
                        {companyInfo.city}<br />
                        CEP {companyInfo.zipCode}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-green-600 p-3 rounded-lg flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">E-mail</h3>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        onClick={handleEmailClick}
                        className="text-gray-600 hover:text-green-600 transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-yellow-600 p-3 rounded-lg flex-shrink-0">
                      <Clock className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Horário de Funcionamento</h3>
                      <p className="text-gray-600">{companyInfo.hours}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Envie uma Mensagem</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nome Completo *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Seu nome"
                        className="h-12"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          E-mail *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="seu@email.com"
                          className="h-12"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Telefone *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          required
                          placeholder="(00) 00000-0000"
                          maxLength={15}
                          className="h-12"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Assunto *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Sobre o que você quer falar?"
                        className="h-12"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensagem *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Digite sua mensagem..."
                        rows={6}
                        className="resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-lg py-6"
                    >
                      <Send className="mr-2" size={20} />
                      Enviar Mensagem
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nossa Localização</h2>
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3977.0!2d-49.8776!3d-6.4969!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x92c5ed45f8e9d1f7%3A0x0!2sAv.%20Weyne%20Cavalcante%2C%20127%20-%20Centro%2C%20Cana%C3%A3%20dos%20Caraj%C3%A1s%20-%20PA!5e0!3m2!1spt-BR!2sbr!4v1704931200000!5m2!1spt-BR!2sbr"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Casa das Ferramentas - Av. Weyne Cavalcante, 127"
            ></iframe>
          </div>
          <div className="mt-4 text-center">
            <a 
              href="https://www.google.com/maps/search/Av.+Weyne+Cavalcante,+127,+Centro,+Canaã+dos+Carajás+-+PA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold"
            >
              <MapPin size={18} />
              Abrir no Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
